import { supabase } from '../lib/supabase'
import { AircraftTransaction, AircraftTransactionInput } from '../types/database'
import { AircraftService } from './intellijetnet/aircraft.service'
import { OverridesService } from './overrides.service'
import { ConsolidatedTransaction } from '../utils/transaction-consolidation'

export class AircraftTransactionsService {
  private static readonly TABLE_NAME = 'aircraft_transactions'

  /**
   * Create a new aircraft transaction (sale)
   */
  static async create(input: AircraftTransactionInput): Promise<AircraftTransaction> {
    try {
      // Get current aircraft data to create snapshot
      const aircraftData = await AircraftService.getById(input.aircraft_id)
      const overrideData = await OverridesService.getByAircraftId(input.aircraft_id)

      // Get aircraft options from local database if not provided in input
      let aircraftOptions: string[] = input.options ?? []
      if (aircraftOptions.length === 0) {
        try {
          const { data: aircraftRecord } = await supabase
            .from('aircraft')
            .select('options')
            .eq('aircraft_id', input.aircraft_id)
            .maybeSingle()

          aircraftOptions = aircraftRecord?.options || []
        } catch (error) {
          // Could not fetch aircraft options for snapshot
        }
      }

      // Helper function to truncate strings to fit database constraints
      const truncateString = (value: string | null | undefined, maxLength: number): string | null => {
        if (!value) return null
        return value.length > maxLength ? value.substring(0, maxLength) : value
      }

      // Prepare transaction data with snapshot
      const transactionData = {
        aircraft_id: input.aircraft_id,
        model_id: input.model_id,
        transaction_type: 'sale' as const,
        transaction_date: input.transaction_date,
        sale_price: input.sale_price,
        api_transaction_id: input.api_transaction_id,
        transaction_notes: input.transaction_notes,
        
        // Use provided data or snapshot from current aircraft/override data (with proper truncation)
        asking_price: input.asking_price ?? overrideData?.asking_price,
        take_price: input.take_price ?? overrideData?.take_price,
        aftt: input.aftt ?? overrideData?.aftt ?? aircraftData?.aftt,
        yeardlv: input.yeardlv ?? aircraftData?.yeardlv,
        sernbr: truncateString(input.sernbr ?? aircraftData?.sernbr, 50),
        regnbr: truncateString(input.regnbr ?? aircraftData?.regnbr, 20),
        make: truncateString(input.make ?? aircraftData?.make, 100),
        model: truncateString(input.model ?? aircraftData?.model, 100),
        base_airport: truncateString(input.base_airport ?? overrideData?.base_airport ?? aircraftData?.baseairport, 10),
        base_city: truncateString(input.base_city ?? overrideData?.base_city ?? aircraftData?.basecity, 100),
        base_state: truncateString(input.base_state ?? overrideData?.base_state ?? aircraftData?.basestate, 50),
        base_country: truncateString(input.base_country ?? overrideData?.base_country ?? aircraftData?.basecountry, 50),
        damage_history: truncateString(input.damage_history ?? overrideData?.damage_history, 20),
        emp: truncateString(input.emp ?? overrideData?.emp, 100),
        status: truncateString(input.status ?? overrideData?.status, 50),
        maintenance_program: truncateString(input.maintenance_program ?? overrideData?.maintenance_program, 100),

        // Enhanced override fields for comprehensive snapshots
        external_asking_price: input.external_asking_price ?? overrideData?.external_asking_price,
        external_take_price: input.external_take_price ?? overrideData?.external_take_price,
        external_sale_price: input.external_sale_price,
        tbo: input.tbo ?? overrideData?.tbo,
        landings: input.landings ?? overrideData?.landings,
        pax: input.pax ?? overrideData?.pax,
        owner: input.owner ?? overrideData?.owner,
        broker: input.broker ?? overrideData?.broker,
        owner_custom: input.owner_custom ?? overrideData?.owner_custom,
        broker_custom: input.broker_custom ?? overrideData?.broker_custom,
        for_sale: input.for_sale ?? overrideData?.for_sale,
        leased: input.leased ?? overrideData?.leased,
        date_listed: input.date_listed ?? overrideData?.date_listed ?? aircraftData?.datelisted,
        date_purchased: input.date_purchased ?? overrideData?.date_purchased,
        custom_1_value: input.custom_1_value ?? overrideData?.custom_1_value,
        custom_2_value: input.custom_2_value ?? overrideData?.custom_2_value,
        custom_3_value: input.custom_3_value ?? overrideData?.custom_3_value,
        options: aircraftOptions
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert(transactionData)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create transaction: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get a specific transaction by ID
   */
  static async getById(transactionId: string): Promise<AircraftTransaction | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('transaction_id', transactionId)
        .maybeSingle()

      if (error) {
        throw new Error(`Failed to fetch transaction: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all transactions for a specific aircraft
   */
  static async getByAircraftId(aircraftId: number): Promise<AircraftTransaction[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('transaction_date', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch transactions: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all transactions for a specific model
   */
  static async getByModelId(modelId: number): Promise<AircraftTransaction[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('model_id', modelId)
        .order('transaction_date', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch transactions: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }



  /**
   * Update a transaction
   */
  static async update(transactionId: string, updates: Partial<AircraftTransactionInput>): Promise<AircraftTransaction> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update(updates)
        .eq('transaction_id', transactionId)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update transaction: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete a transaction
   */
  static async delete(transactionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .delete()
        .eq('transaction_id', transactionId)

      if (error) {
        throw new Error(`Failed to delete transaction: ${error.message}`)
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Get transactions within a date range
   */
  static async getByDateRange(
    startDate: string, 
    endDate: string, 
    modelId?: number
  ): Promise<AircraftTransaction[]> {
    try {
      let query = supabase
        .from(this.TABLE_NAME)
        .select('*')
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)

      if (modelId) {
        query = query.eq('model_id', modelId)
      }

      const { data, error } = await query.order('transaction_date', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch transactions: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get recent transactions (last N days)
   */
  static async getRecent(days: number = 365, modelId?: number): Promise<AircraftTransaction[]> {
    const endDate = new Date().toISOString().split('T')[0]
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    return this.getByDateRange(startDate, endDate, modelId)
  }

  /**
   * Get recent "Full Sale" transactions only (filters out other transaction types)
   * Default to 48 months (1440 days) for Recently Sold container
   */
  static async getRecentFullSales(days: number = 1440, modelId?: number): Promise<AircraftTransaction[]> {
    try {
      let query = supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('transaction_type', 'sale') // Our aircraft_transactions table only stores 'sale' which represents full sales

      if (modelId) {
        query = query.eq('model_id', modelId)
      }

      // Add date filtering
      const endDate = new Date().toISOString().split('T')[0]
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      query = query
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)
        .order('transaction_date', { ascending: false })

      const { data, error } = await query

      if (error) {
        throw new Error(`Failed to fetch full sale transactions: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get recent manual snapshots only (excludes automatic snapshots created from API transactions)
   * These are snapshots created when manually moving aircraft to sold container
   * Default to 48 months (1440 days) for Recently Sold container
   */
  static async getManualSnapshots(days: number = 1440, modelId?: number): Promise<AircraftTransaction[]> {
    try {
      let query = supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('transaction_type', 'sale')
        .is('api_transaction_id', null) // Only manual snapshots (no API transaction reference)

      if (modelId) {
        query = query.eq('model_id', modelId)
      }

      // Add date filtering
      const endDate = new Date().toISOString().split('T')[0]
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      query = query
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)
        .order('transaction_date', { ascending: false })

      const { data, error } = await query

      if (error) {
        throw new Error(`Failed to fetch manual snapshots: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get transaction statistics for a model
   */
  static async getStatsByModelId(modelId: number): Promise<{
    total: number
    last30Days: number
    last90Days: number
    last365Days: number
    averageSalePrice?: number
  }> {
    try {
      const allTransactions = await this.getByModelId(modelId)
      const now = new Date()
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)

      const last30Days = allTransactions.filter(t => new Date(t.transaction_date) >= thirtyDaysAgo).length
      const last90Days = allTransactions.filter(t => new Date(t.transaction_date) >= ninetyDaysAgo).length
      const last365Days = allTransactions.filter(t => new Date(t.transaction_date) >= oneYearAgo).length

      // Calculate average sale price (excluding null values)
      const transactionsWithPrice = allTransactions.filter(t => t.sale_price !== null && t.sale_price !== undefined)
      const averageSalePrice = transactionsWithPrice.length > 0
        ? transactionsWithPrice.reduce((sum, t) => sum + (t.sale_price || 0), 0) / transactionsWithPrice.length
        : undefined

      return {
        total: allTransactions.length,
        last30Days,
        last90Days,
        last365Days,
        averageSalePrice
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if an aircraft has any transactions
   */
  static async hasTransactions(aircraftId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('transaction_id')
        .eq('aircraft_id', aircraftId)
        .limit(1)

      if (error) {
        throw new Error(`Failed to check transactions: ${error.message}`)
      }

      return (data || []).length > 0
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if a snapshot already exists for a specific API transaction
   */
  static async hasSnapshotForApiTransaction(apiTransactionId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('transaction_id')
        .eq('api_transaction_id', apiTransactionId)
        .limit(1)

      if (error) {
        throw new Error(`Failed to check API transaction snapshot: ${error.message}`)
      }

      return (data || []).length > 0
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if any snapshots exist for a list of API transaction IDs
   */
  static async hasSnapshotsForApiTransactions(apiTransactionIds: number[]): Promise<Set<number>> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('api_transaction_id')
        .in('api_transaction_id', apiTransactionIds)

      if (error) {
        throw new Error(`Failed to check API transaction snapshots: ${error.message}`)
      }

      return new Set((data || []).map(row => row.api_transaction_id).filter(id => id !== null))
    } catch (error) {
      throw error
    }
  }

  /**
   * Get a transaction snapshot by API transaction ID
   */
  static async getByApiTransactionId(apiTransactionId: number): Promise<AircraftTransaction | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('api_transaction_id', apiTransactionId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          return null
        }
        throw new Error(`Failed to fetch transaction: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create a consolidated transaction snapshot from multiple API transactions
   */
  static async createConsolidatedSnapshot(consolidatedTransaction: ConsolidatedTransaction): Promise<AircraftTransaction> {
    try {
      // Get current aircraft data to create snapshot
      const aircraftData = await AircraftService.getById(consolidatedTransaction.aircraft_id)
      const overrideData = await OverridesService.getByAircraftId(consolidatedTransaction.aircraft_id)

      if (!aircraftData) {
        throw new Error(`Aircraft data not found for aircraft_id ${consolidatedTransaction.aircraft_id}`)
      }

      // Get aircraft options (this was missing!)
      let aircraftOptions: string[] = []
      try {
        const { data: aircraftRecord } = await supabase
          .from('aircraft')
          .select('options')
          .eq('aircraft_id', consolidatedTransaction.aircraft_id)
          .maybeSingle()

        aircraftOptions = aircraftRecord?.options || []
      } catch (error) {
        // Could not fetch aircraft options for consolidated snapshot
      }

      // Helper function to truncate strings to fit database constraints
      const truncateString = (value: string | null | undefined, maxLength: number): string | null => {
        if (!value) return null
        return value.length > maxLength ? value.substring(0, maxLength) : value
      }

      const transactionData: AircraftTransactionInput = {
        aircraft_id: consolidatedTransaction.aircraft_id,
        model_id: consolidatedTransaction.model_id,
        transaction_date: consolidatedTransaction.trans_date,
        sale_price: consolidatedTransaction.sold_price,

        // API transaction reference - use the primary (newest) transaction ID for consolidated snapshots
        api_transaction_id: consolidatedTransaction.trans_id,

        // Consolidation metadata
        source_transaction_ids: consolidatedTransaction.source_transaction_ids,
        is_consolidated: consolidatedTransaction.is_consolidated,
        consolidation_count: consolidatedTransaction.consolidation_count,

        // Basic aircraft data with safe truncation
        sernbr: truncateString(aircraftData.sernbr, 50),
        regnbr: truncateString(aircraftData.regnbr, 20),
        make: truncateString(aircraftData.make, 100),
        model: truncateString(aircraftData.model, 100),
        yeardlv: aircraftData.yeardlv,

        // Capture all override data or fall back to API data (matching manual snapshot creation)
        asking_price: overrideData?.asking_price ?? consolidatedTransaction.asking_price,
        take_price: overrideData?.take_price,
        external_asking_price: overrideData?.external_asking_price,
        external_take_price: overrideData?.external_take_price,
        external_sale_price: null, // Will be set when user enters sold price

        aftt: overrideData?.aftt ?? aircraftData.aftt,
        tbo: overrideData?.tbo,
        landings: overrideData?.landings,
        pax: overrideData?.pax,

        base_airport: truncateString(overrideData?.base_airport ?? aircraftData.baseairport, 10),
        base_city: truncateString(overrideData?.base_city ?? aircraftData.basecity, 100),
        base_state: truncateString(overrideData?.base_state ?? aircraftData.basestate, 50),
        base_country: truncateString(overrideData?.base_country ?? aircraftData.basecountry, 50),

        damage_history: truncateString(overrideData?.damage_history, 20),
        emp: truncateString(overrideData?.emp, 100),
        status: truncateString(overrideData?.status, 50),
        maintenance_program: truncateString(overrideData?.maintenance_program, 100),

        for_sale: overrideData?.for_sale,
        leased: overrideData?.leased,
        date_listed: overrideData?.date_listed ?? consolidatedTransaction.list_date ?? aircraftData.datelisted,
        date_purchased: overrideData?.date_purchased,

        // Owner/Broker data (these were missing!)
        owner: overrideData?.owner,
        broker: overrideData?.broker,
        owner_custom: overrideData?.owner_custom,
        broker_custom: overrideData?.broker_custom,

        // Custom columns (these were missing!)
        custom_1_value: overrideData?.custom_1_value,
        custom_2_value: overrideData?.custom_2_value,
        custom_3_value: overrideData?.custom_3_value,

        // Options snapshot (this was missing!)
        options: aircraftOptions,

        transaction_notes: consolidatedTransaction.is_consolidated
          ? `Consolidated snapshot from ${consolidatedTransaction.consolidation_count} transactions: ${consolidatedTransaction.source_transaction_ids.join(', ')}`.substring(0, 1000)
          : `Snapshot for transaction ${consolidatedTransaction.trans_id}`.substring(0, 1000)
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert(transactionData)
        .select()
        .single()

      if (error) {
        // Check if this is a unique constraint violation for api_transaction_id
        if (error.code === '23505' && error.message.includes('unique_api_transaction_id')) {
          // Try to fetch the existing snapshot
          const existingSnapshot = await this.getByApiTransactionId(consolidatedTransaction.trans_id)
          if (existingSnapshot) {
            return existingSnapshot
          }
        }

        throw new Error(`Failed to create consolidated snapshot: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }
}
